/**
 * Dates and times on the receipt.
 *
 * The two halves come from different places, deliberately.
 *
 * The TIME ZONE is the location's. A receipt records something a restaurant did at a moment on its own clock,
 * and a guest who pays in Austin and reads the receipt on a phone still set to London should see the Austin
 * time — that is what the restaurant will find it under if anyone ever has to look it up.
 *
 * The LANGUAGE is the guest's, which is where this differs from money. An amount is a figure that has to
 * match the paper on the table, so it follows the location. A date is prose: "Mar" is an English word, and
 * sitting it in the middle of a Spanish receipt reads as a string somebody forgot to translate.
 */

/** Cached because constructing a DateTimeFormat is expensive and the receipt re-renders. */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(locale: string, timeZone: string | undefined): Intl.DateTimeFormat {
    const key = `${locale}|${timeZone ?? ''}`;
    const cached = formatterCache.get(key);
    if (cached) return cached;

    const created = new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone,
    });

    formatterCache.set(key, created);
    return created;
}

/**
 * Formats an ISO instant for the receipt, or returns null if it is not one.
 *
 * Null rather than a fallback string: a receipt line that reads "Invalid Date" is worse than a receipt with
 * no date line, and the caller can leave the row out.
 *
 * An unusable IANA zone falls back to the viewer's own rather than failing — `Intl` throws on a zone it does
 * not recognise, and one bad row of location config should not take down the screen that proves a guest paid.
 */
export function formatReceiptDateTime(iso: string, locale: string, timeZoneId?: string): string | null {
    const instant = new Date(iso);
    if (Number.isNaN(instant.getTime())) return null;

    try {
        return formatter(locale, timeZoneId).format(instant);
    } catch {
        return formatter(locale, undefined).format(instant);
    }
}
